import * as THREE from 'three';
import { Kit } from '../seasons/kit.ts';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from './glow.ts';

/**
 * Clues on the field (a side saga, docs/SAGAS.md): something someone dropped lies on the ground (a torn
 * shawl and a lantern on its side), a soft column of pale light stands over it so it is found from the table camera, and a
 * ring fills round it while a hero stands there (`clues.hold`, of SIDE.clueTicks). Picked up, it flares and
 * goes. One merged model and one ring per clue (three at most), one glow layer for every light.
 */

const HOLD_TICKS = 12;
const PALE = new THREE.Color('#cfe0ff');
const RED = new THREE.Color('#ff4f3a');
const WARM = new THREE.Color('#ffd6a0');

const RING_FRAG = /* glsl */ `
uniform float uFill;
uniform float uTime;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float band = smoothstep(0.84, 0.87, r) * (1.0 - smoothstep(0.95, 0.98, r));
  float ang = fract(atan(p.x, p.y) / 6.2831853 + 0.5);
  float filled = step(ang, uFill);
  float a = band * mix(0.16 + 0.08 * sin(uTime * 3.0), 0.95, filled);
  vec3 c = mix(vec3(0.75, 0.82, 0.95), vec3(1.0, 0.54, 0.43), filled);
  if (a < 0.01) discard;
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;

/** The dropped things, merged by material into four meshes (built once, cloned per clue). */
const dropped = (): THREE.Group => {
  const parts: Record<string, THREE.BufferGeometry[]> = {
    shawl: [],
    fringe: [],
    iron: [],
    glass: [],
  };
  const put = (geo: THREE.BufferGeometry, key: string, o: THREE.Object3D): void => {
    o.updateMatrixWorld(true);
    (parts[key] as THREE.BufferGeometry[]).push(geo.applyMatrix4(o.matrixWorld));
  };
  // A torn shawl, crumpled: a few flat folds of dull red wool, a dark fringe.
  for (const [x, z, r, s] of [
    [0, 0, 0.3, 1],
    [0.12, 0.08, 1.2, 0.8],
    [-0.1, 0.1, 2.1, 0.7],
    [0.05, -0.12, 2.9, 0.65],
  ] as const) {
    const o = new THREE.Object3D();
    o.position.set(x, 0.02 + s * 0.015, z);
    o.rotation.set((s - 0.8) * 0.4, r, (0.9 - s) * 0.5);
    put(new THREE.BoxGeometry(0.3 * s, 0.025, 0.22 * s), 'shawl', o);
  }
  for (let i = 0; i < 5; i++) {
    const o = new THREE.Object3D();
    o.position.set(-0.17 + i * 0.025, 0.012, 0.15);
    o.rotation.y = (i - 2) * 0.15;
    put(new THREE.BoxGeometry(0.012, 0.01, 0.07), 'fringe', o);
  }
  // A lantern on its side, its glass cracked, still faintly lit.
  const lan = new THREE.Object3D();
  lan.position.set(0.22, 0.07, -0.05);
  lan.rotation.set(0, 0.6, Math.PI / 2 - 0.2);
  const sub = (x: number, y: number, z: number, ry = 0): THREE.Object3D => {
    const o = new THREE.Object3D();
    o.position.set(x, y, z);
    o.rotation.y = ry;
    lan.add(o);
    return o;
  };
  put(new THREE.BoxGeometry(0.11, 0.02, 0.11), 'iron', sub(0, 0, 0));
  put(new THREE.ConeGeometry(0.08, 0.06, 4), 'iron', sub(0, 0.14, 0, Math.PI / 4));
  put(new THREE.BoxGeometry(0.09, 0.1, 0.09), 'glass', sub(0, 0.06, 0));
  for (const [x, z] of [
    [-0.05, -0.05],
    [0.05, -0.05],
    [-0.05, 0.05],
    [0.05, 0.05],
  ] as const)
    put(new THREE.BoxGeometry(0.012, 0.11, 0.012), 'iron', sub(x, 0.06, z));
  // One mesh, painted by vertex colour (the lantern's glass bright; its glow sprite does the rest).
  const COL: Record<string, string> = { shawl: '#8e3a32', fringe: '#5a2a24', iron: '#2a2b30', glass: '#ffd9a0' };
  const kit = new Kit();
  const id = new THREE.Matrix4();
  for (const [k, list] of Object.entries(parts)) for (const x of list) kit.add(x, COL[k] as string, id);
  for (const list of Object.values(parts)) for (const x of list) x.dispose();
  const g = new THREE.Group();
  const mesh = kit.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }), 'clue-dropped');
  if (mesh) g.add(mesh);
  return g;
};

interface Mark {
  id: string;
  root: THREE.Group;
  ring: THREE.Mesh;
  ringMat: THREE.ShaderMaterial;
  p: THREE.Vector3;
  fill: number;
  gone: boolean;
  fade: number;
}

export class ClueMarks {
  readonly group = new THREE.Group();
  private marks: Mark[] = [];
  private glow = new GlowLayer(32);
  private proto = dropped();
  private ringGeo = new THREE.PlaneGeometry(1.7, 1.7).rotateX(-Math.PI / 2);

  constructor(
    s: BattleState,
    at: (x: number, y: number, h?: number) => THREE.Vector3,
    private low: boolean,
  ) {
    this.group.name = 'clues';
    for (const c of s.rules?.clues ?? []) {
      const p = at(c.x, c.y);
      const root = this.proto.clone();
      root.position.copy(p);
      root.scale.setScalar(1.7);
      root.rotation.y = (c.x * 0.37 + c.y * 0.11) % (Math.PI * 2);
      const ringMat = new THREE.ShaderMaterial({
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
        fragmentShader: RING_FRAG,
        uniforms: { uFill: { value: 0 }, uTime: { value: 0 } },
        transparent: true,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(this.ringGeo, ringMat);
      ring.position.copy(p).setY(p.y + 0.03);
      ring.renderOrder = 7;
      this.group.add(root, ring);
      this.marks.push({
        id: c.id,
        root,
        ring,
        ringMat,
        p,
        fill: 0,
        gone: false,
        fade: 1,
      });
    }
    this.group.add(this.glow.mesh);
  }

  /**
   * Every fresh sim tick: how far each one is held, and the ones just picked up (`onFound` flares them).
   */
  tick(s: BattleState, onFound: (p: THREE.Vector3) => void): void {
    const list = s.rules?.clues ?? [];
    for (let i = 0; i < this.marks.length; i++) {
      const m = this.marks[i] as Mark;
      const found = !!s.clues?.found.some((f) => f.id === m.id);
      if (found && !m.gone) {
        m.gone = true;
        onFound(m.p.clone().setY(m.p.y + 0.4));
      }
      const k = list.findIndex((c) => c.id === m.id);
      m.fill = found ? 1 : Math.min(1, (s.clues?.hold[k] ?? 0) / HOLD_TICKS);
    }
  }

  frame(t: number): void {
    this.glow.begin();
    for (const m of this.marks) {
      if (m.gone) m.fade = Math.max(0, m.fade - 0.04);
      const vis = m.fade > 0.001;
      m.root.visible = m.ring.visible = vis;
      if (!vis) continue;
      m.root.scale.setScalar(1.7 * (m.gone ? 0.6 + 0.4 * m.fade : 1));
      (m.ringMat.uniforms.uTime as { value: number }).value = t;
      (m.ringMat.uniforms.uFill as { value: number }).value = m.fill;
      const pulse = 0.8 + 0.2 * Math.sin(t * 2.2 + m.p.x);
      // Something to pick up, not smoke: a short cold shimmer standing on it, a red heartbeat on the ground under it
      // (glow sprites: no mesh of their own).
      for (let k = 1; k <= 3; k++) this.glow.add(m.p.x, m.p.y + k * 0.2, m.p.z, PALE, 0.42 - k * 0.06, (0.5 - k * 0.12) * pulse * m.fade);
      const beat = Math.pow(Math.max(0, Math.sin(t * 3.1 + m.p.x)), 6);
      this.glow.add(m.p.x, m.p.y + 0.06, m.p.z, RED, 0.9 + beat * 0.35, (0.2 + beat * 0.3) * m.fade);
      this.glow.add(m.p.x + 0.1, m.p.y + 0.1, m.p.z - 0.03, WARM, 0.35, 0.7 * m.fade);
      // A glint now and then, just over it, to catch the eye.
      const glint = Math.max(0, Math.sin(t * 1.3 + m.p.z) - 0.9) * 10;
      if (glint > 0) this.glow.add(m.p.x, m.p.y + 0.55, m.p.z, PALE, 0.45, glint * 0.7 * m.fade);
    }
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.marks) m.ringMat.dispose();
    this.proto.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material | undefined)?.dispose?.();
    });
    this.ringGeo.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
